import { useEffect, useRef } from "react";
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from "react";

/** ~400 ms: past a tap, short of the OS's own long-press menus (iOS ~500, Android ~500). */
export const LONG_PRESS_MS = 400;
/** A finger that travels this far is scrolling the grid, not holding a card. */
const SLOP_PX = 10;

/**
 * A long-press on TOUCH only — the mobile way into select mode (issue #12). A mouse has
 * the hover checkbox and the Select button; a long mouse press is a drag, not a gesture.
 *
 * Spread the returned handlers on the element. When the press fires, the click that the
 * same finger produces on lift is swallowed in the capture phase, so it neither opens
 * the clip's popup nor toggles the card it just selected. The OS's own long-press
 * responses (the image callout, a context menu) are held back while a press is armed.
 * `enabled: false` returns inert handlers, so cards outside a collection page are
 * untouched.
 */
export function useLongPress(onLongPress: () => void, enabled = true) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const cb = useRef(onLongPress);
  cb.current = onLongPress;

  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  };
  useEffect(() => cancel, []);

  if (!enabled) return {};
  return {
    onPointerDown: (e: ReactPointerEvent) => {
      if (e.pointerType !== "touch") return;
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = setTimeout(() => {
        timer.current = null;
        fired.current = true;
        navigator.vibrate?.(10);
        cb.current();
      }, LONG_PRESS_MS);
    },
    onPointerMove: (e: ReactPointerEvent) => {
      const s = start.current;
      if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > SLOP_PX) cancel();
    },
    onPointerUp: cancel,
    // A scroll that takes over the gesture arrives as a cancel.
    onPointerCancel: cancel,
    onContextMenu: (e: ReactMouseEvent) => {
      if (timer.current || fired.current) e.preventDefault();
    },
    onClickCapture: (e: ReactMouseEvent) => {
      if (!fired.current) return;
      fired.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
  };
}
